import { useState } from 'react';
import { X, Globe, File, StickyNote, Eye, Tag, Save, Edit2, ExternalLink, FileText, Highlighter } from 'lucide-react';
import DualModeEditor from '../ui/DualModeEditor';
import useResourceStore from '../../stores/useResourceStore';

const RES_TYPES = {
    note: { label: 'Note', icon: StickyNote, color: 'var(--caution)' },
    link: { label: 'Link', icon: Globe, color: 'var(--highlight-cyan)' },
    file: { label: 'File', icon: File, color: 'var(--info)' },
};

/**
 * ProjectResourceModal — Handles resource create, edit, and view modals.
 * Extracted from ProjectDetail.jsx for maintainability.
 */
export default function ProjectResourceModal({
    mode, // 'create' | 'edit' | 'view'
    resource, // existing resource for edit/view, null for create
    projectId,
    onClose,
    onEdit, // called from view mode to switch to edit
    onDistill, // called from view mode to open distillation
}) {
    const addResource = useResourceStore(s => s.addResource);
    const updateResource = useResourceStore(s => s.updateResource);
    // Form state
    const [resType, setResType] = useState(resource?.type || 'note');
    const [resTitle, setResTitle] = useState(resource?.title || '');
    const [resContent, setResContent] = useState(
        mode === 'edit' && resource?.type === 'link' ? null : (resource?.content || null)
    );
    const [resSummary, setResSummary] = useState(resource?.summary || '');
    const [resUrl, setResUrl] = useState(
        resource?.type === 'link' ? (resource?.content || '') : ''
    );
    const [resTags, setResTags] = useState(resource?.tags?.join(', ') || '');

    const handleSave = () => {
        if (!resTitle.trim()) return;
        const tags = resTags.split(',').map(t => t.trim()).filter(Boolean);
        const noteContent = resContent && typeof resContent === 'object'
            ? JSON.stringify(resContent)
            : (resContent || '');

        const data = {
            title: resTitle.trim(),
            summary: resSummary.trim(),
            content: resType === 'link' ? resUrl.trim() : noteContent,
            type: resType,
            tags,
            projectId,
        };

        if (resource?.id) {
            updateResource(resource.id, data);
        } else {
            addResource(data);
        }
        
        onClose();
    };

    // --- VIEW MODE ---
    if (mode === 'view' && resource) {
        return (
            <div className="modal-overlay" onClick={onClose}>
                <div className="modal-content animate-fade-in-scale" onClick={e => e.stopPropagation()} style={{ maxWidth: '640px' }}>
                    <div className="modal-header">
                        <div className="resource-card__type" style={{ '--type-color': RES_TYPES[resource.type]?.color }}>
                            {(() => { const Icon = RES_TYPES[resource.type]?.icon || FileText; return <Icon size={14} />; })()}
                            <span>{RES_TYPES[resource.type]?.label}</span>
                        </div>
                        <div className="res-view-modal__actions">
                            <button className="btn btn-ghost btn-sm" onClick={() => onEdit(resource)}>
                                <Edit2 size={14} /> Edit
                            </button>
                            <button className="btn btn-ghost btn-icon" onClick={onClose}>
                                <X size={18} />
                            </button>
                        </div>
                    </div>
                    
                    <div className="modal-body">
                    <h2 className="res-view-modal__title">{resource.title}</h2>
                    {resource.summary && (
                        <div className="res-view-modal__summary">
                            <span className="res-view-modal__summary-label">Executive Summary</span>
                            <p>{resource.summary}</p>
                        </div>
                    )}
                    {resource.type === 'link' && resource.content && (
                        <a href={resource.content} target="_blank" rel="noopener noreferrer" className="res-view-modal__link">
                            <ExternalLink size={16} /> {resource.content}
                        </a>
                    )}
                    {resource.type === 'note' && resource.content && (
                        <div className="res-view-modal__content">
                            <DualModeEditor content={resource.content} editable={false} />
                            <button
                                className="btn btn-primary btn-sm"
                                style={{ marginTop: 'var(--space-sm)' }}
                                onClick={() => onDistill(resource)}
                            >
                                <Highlighter size={14} /> Open Distillation Mode
                            </button>
                        </div>
                    )}
                    {resource.type === 'file' && resource.content && (
                        <div className="res-view-modal__file-ref">
                            <File size={16} /> <span>{resource.content}</span>
                        </div>
                    )}
                    {resource.tags?.length > 0 && (
                        <div className="resource-card__tags" style={{ padding: 'var(--space-sm) 0' }}>
                            {resource.tags.map(tag => (
                                <span key={tag} className="resource-card__tag">#{tag}</span>
                            ))}
                        </div>
                    )}
                    </div>
                </div>
            </div>
        );
    }

    // --- CREATE / EDIT MODE ---
    return (
        <div className="modal-overlay" onClick={() => {
            const isDirty = resTitle.trim() || resUrl.trim() || resSummary.trim() || resTags.trim() || (resType !== 'note' && resContent);
            if (isDirty && !window.confirm("Discard unsaved resource?")) return;
            onClose();
        }}>
            <div className="modal-content animate-fade-in-scale" onClick={e => e.stopPropagation()} style={{ maxWidth: '640px' }} onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA' && resType !== 'note') {
                    e.preventDefault();
                    if (resTitle.trim()) handleSave();
                }
            }}>
                <div className="modal-header">
                    <h2>{resource ? 'Edit Resource' : 'New Resource'}</h2>
                    <button className="btn btn-ghost btn-icon" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>
                <div className="res-modal__types">
                    {Object.entries(RES_TYPES).map(([key, cfg]) => {
                        const Icon = cfg.icon;
                        return (
                            <button
                                key={key}
                                className={`res-modal__type-btn ${resType === key ? 'res-modal__type-btn--active' : ''}`}
                                style={{ '--type-c': cfg.color }}
                                onClick={() => setResType(key)}
                            >
                                <Icon size={18} />
                                <span className="res-modal__type-label">{cfg.label}</span>
                            </button>
                        );
                    })}
                </div>
                
                <div className="modal-body">
                    <div className="res-modal__field">
                        <label>Title</label>
                        <input className="input" value={resTitle} onChange={e => setResTitle(e.target.value)} placeholder="Resource title" autoFocus />
                    </div>
                    {resType === 'link' && (
                        <div className="res-modal__field">
                            <label><Globe size={14} /> URL</label>
                            <input className="input" value={resUrl} onChange={e => setResUrl(e.target.value)} placeholder="https://..." type="url" />
                        </div>
                    )}
                    {resType === 'file' && (
                        <div className="res-modal__field">
                            <label><File size={14} /> File reference</label>
                            <input className="input" value={resContent} onChange={e => setResContent(e.target.value)} placeholder="File path or description..." />
                        </div>
                    )}
                    <div className="res-modal__field">
                        <label className="res-modal__label-fancy"><Eye size={14} /> Executive Summary</label>
                        <textarea className="input res-modal__summary" value={resSummary} onChange={e => setResSummary(e.target.value)} placeholder="Key takeaway in 1-2 sentences" rows={2} />
                    </div>
                    {resType === 'note' && (
                        <div className="res-modal__field">
                            <label>Content</label>
                            <DualModeEditor
                                content={resContent}
                                onUpdate={setResContent}
                                placeholder="Start writing, or type / for commands..."
                            />
                        </div>
                    )}
                    <div className="res-modal__field">
                        <label><Tag size={14} /> Tags</label>
                        <input className="input" value={resTags} onChange={e => setResTags(e.target.value)} placeholder="Comma-separated, e.g. design, reference" />
                    </div>
                </div>
                
                <div className="modal-footer">
                    <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
                    <button className="btn btn-primary" onClick={handleSave} disabled={!resTitle.trim()}>
                        <Save size={16} /> {resource ? 'Save' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
}
